import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';
import { readableColor } from 'polished';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { fixPropWarnings } from '../utils/fixPropWarnings';

import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import InlineLink from './InlineLink';

const Section = styled.section`
  background-color: ${({ background }) => themeGet(`colors.${background}`)};
  color: ${({ theme, background }) => readableColor(
    theme.colors[background],
    theme.colors.black,
    theme.colors.white,
  )};

  padding: 40px 0;
  overflow: hidden;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 40px 0 140px 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0 100px 0;
  }
`;

const Heading = styled.h1`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading1.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading1.letterSpacing')};
  text-align: center;
  padding: 0 20px;

  &.heroBannerV2 {
    text-align: left;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};

    &.heroBannerV2 {
      text-align: center;
      margin-left: 0;
    }
  }
`;

const SubHeading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.desktop.body2.fontSize')};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 335px;
  position: relative;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 710px;
    height: 353px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    width: 860px;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  width: 335px;
  border-radius: 20px;
  z-index: 9;
  flex-direction: column;

  img {
    width: 295px;
    margin: 0 auto;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    width: 400px;
    height: 400px;
    img {
      width: 360px;
    }
  }
`;

const BodyCopy = styled.p`
  margin: 10px 0 20px 0;
  font-size: 16px;
  line-height: ${themeGet('typography.mobile.heading5.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading5.letterSpacing')};
  text-align: left;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 20px 0;
    font-size: ${themeGet('typography.desktop.body5.fontSize')};
    line-height: ${themeGet('typography.desktop.heading5.lineHeight')};
  }
`;

const SaveAndEarnOptionsTitle = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_3]: (_, children) => <SubHeading>{children}</SubHeading>,
  },
};

const SaveAndEarnBody = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

const LearnMoreLink = styled(InlineLink)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: ${themeGet('colors.white')};
`;

const Image = styled(GatsbyImage)`
  margin-bottom: 30px;
  overflow: visible;
`;

const AnimationAsset = styled.img.withConfig(fixPropWarnings('isEven'))`
  position: absolute;
  z-index: 10;
  left: ${({ isEven }) => (isEven ? '-15%' : '40%')};
  top: ${({ isEven }) => (isEven ? '0' : '405px')};

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    left: ${({ isEven }) => (isEven ? '-10%' : '72%')};
    top: -25px;
  }
`;

/**
 * Section that displays the hero banner for the page. Uses the HeroBanner content type
 * @param {Object} data - contentful data obj.
 * @param {String} data.sectionId - id of the section for deep linking
 * @param {String} data.backgroundColor - background color to be used for the component
 * @param {{raw: string}} data.heroTitle - Contentful richtext field for the Hero banner title
 * @param {{raw: string}} data.heroText - Contentful richtext field for the Hero banner body copy
 * @param {Object} data.heroLinks - Button /  LinkWrapper Content type reference for the hero CallToAction
 * @param {Object} data.heroImages - DamAsset Content type reference for the hero banner image
 */

const SaveAndEarn = ({ data }) => {
  if (!data) return null;
  const {
    sectionId,
    backgroundColor,
    sectionTitle,
    combinatorSectionItems,
  } = data;
  return (
    <Section id={sectionId} background={backgroundColor}>
      <SectionContainer fluid>
        <RichText document={sectionTitle} options={SaveAndEarnOptionsTitle} />
        <Wrapper>
          {combinatorSectionItems.map(
            ({
              id,
              headingRTF,
              bodyRTF,
              button,
              imageGallery,
            }, index) => (
              <StepsContainer key={id}>
                {
                    imageGallery
                    && imageGallery.map((image) => {
                      const gatsbyImage = getImage(image?.asset?.localImage);
                      return (image.asset.mimeType === 'image/gif')
                        ? (
                          <AnimationAsset
                            key={image.id}
                            src={image.asset.localImage.publicURL}
                            alt={image.altText}
                            isEven={!(index % 2)}
                          />
                        ) : gatsbyImage && (
                        <Image
                          key={image.id}
                          image={gatsbyImage}
                          alt={image.altText || ''}
                        />
                        );
                    })
                  }
                <RichText document={headingRTF} options={SaveAndEarnOptionsTitle} />
                <RichText document={bodyRTF} options={SaveAndEarnBody} />
                {button && (
                <LearnMoreLink key={button.id} href={button.linkUrl}>
                  {button.linkText}
                </LearnMoreLink>
                )}
              </StepsContainer>
            ),
          )}
        </Wrapper>
      </SectionContainer>
    </Section>
  );
};

SaveAndEarn.propTypes = {
  data: PropTypes.shape().isRequired,
};

SaveAndEarn.defaultProps = {
};
export default SaveAndEarn;
